import { useMemo } from 'react'

import {
  CustomFieldSortOptionFragment,
  FieldSortOptionFragment,
  ProductBoardItemFactorFragment,
  ProductBoardItemTableRowDataFragment,
  ProductBoardItemTableRowFragment,
  RoadmapItemCustomColumnFragment,
} from '~generated/operations'
import { SortOrder, ProductBoardItemsSortInput } from '~generated/types'
import { removeEmptyFields } from '~lib/helpers'

export const ProductBoardItemsSortKeys: KeysEnum<ProductBoardItemsSortInput> = {
  orderIndex: true,
  priority: true,
  revenueImpact: true,
  assignee: true,
  createdAt: true,
  customColumn: true,
  effort: true,
  endDate: true,
  factor: true,
  release: true,
  roadmap: true,
  score: true,
  startDate: true,
  status: true,
  title: true,
  value: true,
}

type SortProductBoardItems = {
  items: ProductBoardItemTableRowDataFragment[] | ProductBoardItemTableRowFragment[]
  sort: ProductBoardItemsSortInput
}

const sortCallbacks: Record<
  keyof Required<Omit<ProductBoardItemsSortInput, 'customColumn' | 'factor'>>,
  (a: ProductBoardItemTableRowDataFragment, b: ProductBoardItemTableRowDataFragment, order: SortOrder) => number
> = {
  revenueImpact: (a, b, order) => {
    if (!a.revenueImpactDetails?.totalRevenueImpact && !b.revenueImpactDetails?.totalRevenueImpact) return 0

    if (!a.revenueImpactDetails?.totalRevenueImpact) return 1
    if (!b.revenueImpactDetails?.totalRevenueImpact) return -1

    return (
      (a.revenueImpactDetails.totalRevenueImpact - b.revenueImpactDetails.totalRevenueImpact) *
      (order === SortOrder.ASC ? 1 : -1)
    )
    // if (!a.revenueImpactDetals?.totalRevenueImpact && !b.revenueImpactDetals?.totalRevenueImpact) return 0

    // if (!a.revenueImpactDetals?.totalRevenueImpact) return 1
    // if (!b.revenueImpactDetals?.totalRevenueImpact) return -1

    // if (!a.revenueImpactDetals?.wonDealsRevenueImpact && !b.revenueImpactDetals?.wonDealsRevenueImpact) {
    //   return (
    //     (a.revenueImpactDetals.totalRevenueImpact - b.revenueImpactDetals.totalRevenueImpact) *
    //     (order === SortOrder.ASC ? 1 : -1)
    //   )
    // }

    // if (!a.revenueImpactDetals?.wonDealsRevenueImpact) return 1
    // if (!b.revenueImpactDetals?.wonDealsRevenueImpact) return -1

    // return (
    //   (a.revenueImpactDetals.wonDealsRevenueImpact - b.revenueImpactDetals.wonDealsRevenueImpact) *
    //   (order === SortOrder.ASC ? 1 : -1)
    // )
  },
  assignee: (a, b, order) => {
    if (!a.assignee && !b.assignee) return 0
    if (!a.assignee) return 1
    if (!b.assignee) return -1
    return a.assignee.email.localeCompare(b.assignee.email) * (order === SortOrder.ASC ? 1 : -1)
  },
  createdAt: (a, b) => {
    // return new Date(a.createdAt)
    return a.startDate > b.startDate ? 1 : -1 // TODO
  },
  effort: (a, b, order) => {
    if (!a.effort && !b.effort) return 0
    if (!a.effort) return 1
    if (!b.effort) return -1
    return (a.effort - b.effort) * (order === SortOrder.ASC ? 1 : -1)
  },
  endDate: (a, b, order) => {
    if (a.endDate === b.endDate) return 0

    if (!a.endDate) return 1
    if (!b.endDate) return -1
    return (new Date(a.endDate).getTime() - b.endDate.getTime()) * (order === SortOrder.ASC ? 1 : -1)
  },
  orderIndex: (a, b, order) => {
    return (a.orderIndex > b.orderIndex ? 1 : -1) * (order === SortOrder.ASC ? 1 : -1)
  },
  release: (a, b, order) => {
    if (!a.release && !b.release) return 0
    if (!a.release) return 1
    if (!b.release) return -1
    return a.release.name.localeCompare(b.release.name) * (order === SortOrder.ASC ? 1 : -1)
  },
  roadmap: (a, b, order) => {
    if (!a.roadmap && !b.roadmap) return 0
    if (!a.roadmap) return 1
    if (!b.roadmap) return -1
    return a.roadmap.name.localeCompare(b.roadmap.name) * (order === SortOrder.ASC ? 1 : -1)
  },
  startDate: (a, b, order) => {
    if (a.startDate === b.startDate) return 0

    if (!a.startDate) return 1
    if (!b.startDate) return -1
    return (new Date(a.startDate).getTime() - b.startDate.getTime()) * (order === SortOrder.ASC ? 1 : -1)
  },
  status: (a, b, order) => {
    if (!a.status && !b.status) return 0
    if (!a.status) return 1
    if (!b.status) return -1
    return a.status.name.localeCompare(b.status.name) * (order === SortOrder.ASC ? 1 : -1)
  },
  title: (a, b, order) => {
    return a.title.localeCompare(b.title) * (order === SortOrder.ASC ? 1 : -1)
  },
  priority: (a, b, order) => {
    if (a.priority === b.priority) return 0

    if (!a.priority) return 1
    if (!b.priority) return -1
    return a.priority.localeCompare(b.priority) * (order === SortOrder.ASC ? 1 : -1)
  },
  score: (a, b, order) => {
    if (!a.score && !b.score) return 0
    if (!a.score) return 1
    if (!b.score) return -1
    return (a.score - b.score) * (order === SortOrder.ASC ? 1 : -1)
  },
  value: (a, b, order) => {
    const aImmutableFactor = a.factors.find((f) => f.immutable)
    const bImmutableFactor = b.factors.find((f) => f.immutable)
    return ((aImmutableFactor?.value ?? 0) - (bImmutableFactor?.value ?? 0)) * (order === SortOrder.ASC ? 1 : -1)
  },
}

export function sortProductBoardItems({ items = [], sort }: SortProductBoardItems) {
  const key = (sort && (Object.keys(removeEmptyFields(sort))[0] as keyof ProductBoardItemsSortInput)) || 'orderIndex'

  if (['customColumn', 'factor'].includes(key)) {
    const { order, customFieldId } = sort[key] as CustomFieldSortOptionFragment

    return items.slice().sort((a, b) => {
      //  customColumns or factors
      const arrKey = `${key}s`
      // customColumnId or factorId
      const fieldIdKey = `${key}Id`

      const firstCustomFieldValue =
        (a[arrKey] as (RoadmapItemCustomColumnFragment | ProductBoardItemFactorFragment)[]).find(
          (field) => field[fieldIdKey] === customFieldId
        )?.value || null

      const secondCustomFieldValue =
        (b[arrKey] as (RoadmapItemCustomColumnFragment | ProductBoardItemFactorFragment)[]).find(
          (field) => field[fieldIdKey] === customFieldId
        )?.value || null

      if (firstCustomFieldValue === secondCustomFieldValue) return 0

      if (!firstCustomFieldValue) return 1
      if (!secondCustomFieldValue) return -1
      return (firstCustomFieldValue > secondCustomFieldValue ? 1 : -1) * (order === SortOrder.ASC ? 1 : -1)
    })
  }

  const sortOptions = (sort?.[key] as FieldSortOptionFragment) || { order: SortOrder.ASC }

  const cb = sortCallbacks[key]

  return items.slice().sort((a, b) => {
    return cb(a, b, sortOptions.order)
  })
}

function useSortProductBoardItems({ items = [], sort: sortInput }: SortProductBoardItems) {
  const rootItems = useMemo(() => {
    return items.filter((item) => !item.parentItemId)
  }, [items])

  return useMemo(() => sortProductBoardItems({ items: rootItems, sort: sortInput }), [rootItems, sortInput])
}

export default useSortProductBoardItems
